import { Stack } from '@mui/material';
import { useMemo } from 'react';

import { Loader } from '@/components/progress/Loader/Loader';
import { useSelectedSuggestionEvidenceContext } from '@/modules/aiSuggestions/context/SelectedSuggestionEvidence.context';
import { SelectedSuggestionEvidenceProvider } from '@/modules/aiSuggestions/context/SelectedSuggestionEvidence.provider';
import { SideDrawer } from '@/modules/common/SideDrawer';
import { HouseholdDocumentsViewerProvider } from '@/modules/documents/components/HouseholdDocumentsViewer/context/HouseholdDocumentsViewer.provider';
import { HouseholdDocumentsViewer } from '@/modules/documents/components/HouseholdDocumentsViewer/HouseholdDocumentsViewer';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { getNodes } from '@/utils/graphqlUtils';

import { getAIOnboardingSuggestionDrawerDocumentIds } from './AIOnboardingSuggestionDrawer.utils';
import { useFetchSuggestionForDrawerQuery } from './graphql/AIOnboardingSuggestionDrawer.generated';

export interface AIOnboardingSuggestionDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  suggestionId: string;
}

function AIOnboardingSuggestionDrawerDocuments({
  documentIds,
  householdId,
}: {
  documentIds: string[];
  householdId: string;
}) {
  const {
    documentPanelProps: {
      activeDocumentId,
      activeDocumentViewerTab,
      annotations,
      searchQuery,
      searchLoading,
    },
  } = useSelectedSuggestionEvidenceContext();

  return (
    <HouseholdDocumentsViewerProvider
      householdId={householdId}
      documentIds={documentIds}
      overrideActiveDocumentId={activeDocumentId}
      overrideDocumentViewerActiveTab={activeDocumentViewerTab}
    >
      <HouseholdDocumentsViewer
        overrideSearchQuery={searchQuery}
        overrideSearchLoading={searchLoading}
        overrideAnnotations={annotations}
        documentChromeSx={{ pr: 0 }}
        topRightControlsRightOffset={16}
      />
    </HouseholdDocumentsViewerProvider>
  );
}

export function AIOnboardingSuggestionDrawer({
  isOpen,
  onClose,
  suggestionId,
}: AIOnboardingSuggestionDrawerProps) {
  const { householdId } = useHouseholdDetailsContext();

  const { data, loading } = useFetchSuggestionForDrawerQuery({
    variables: { suggestionId },
    skip: !isOpen,
  });

  const suggestion = getNodes(data?.aiSuggestions)[0];

  const documentIds = useMemo(
    () => getAIOnboardingSuggestionDrawerDocumentIds(suggestion),
    [suggestion]
  );

  if (!householdId) {
    // This is for type-safety only; householdId should always be defined here.
    return null;
  }

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      heading="Source document"
      data-testid="ai-onboarding-suggestion-drawer"
      bodyPropsSx={{
        p: 0,
        overflow: 'hidden',
        minHeight: 0,
      }}
      body={
        <Stack width="100%" flex={1} minHeight={0} overflow="hidden">
          {loading && (
            <Loader
              boxProps={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flex: 1,
              }}
              circularProgressProps={{ size: 40 }}
            />
          )}
          {!loading && suggestion && (
            <SelectedSuggestionEvidenceProvider
              initialSelectedSuggestion={suggestion}
            >
              <AIOnboardingSuggestionDrawerDocuments
                householdId={householdId}
                documentIds={documentIds}
              />
            </SelectedSuggestionEvidenceProvider>
          )}
        </Stack>
      }
    />
  );
}
